/**
 * @file XCOMM Vote related queries.
 */

import gql from 'graphql-tag';

import { IdentityMatchingDetails } from '../fragments/identity';
import { FaceMatchVoteFieldDetails, FaceDetails } from '../fragments/votes';
import { PubBoxDetails } from '../../../../graphql/fragments/pub_box';
import { AlbumMatchingDetails } from '../fragments/album';
import { SelectedIdentityAlbumFolderTagDetailsForMatching } from '../fragments/album-folder-tags';
import { PUB_MATCHING_TIME_PER_VOTE_SEC } from '../../constants/command';
import {
  SelectedIdentityReferenceFacesDetails,
  SelectedIdentityTagDetails,
} from '../fragments/selectedIdentity';
import { PhotoUrlTypeFields } from '../../../../graphql/fragments/album';

export const LeasePossibleVotes = gql`
  query leasePossibleVotes($count: Int, $time_per_vote: Int) {
    leasePossibleVotes(count: $count, time_per_vote: $time_per_vote) {
      lease_id
      lease_expires_at
      selected_identity {
        id
        identity {
          ...IdentityMatchingDetails
        }
        album_folder_tags {
          records {
            ...SelectedIdentityAlbumFolderTagDetailsForMatching
          }
        }
        album_membership {
          album {
            ...AlbumMatchingDetails
          }
        }
        ...SelectedIdentityReferenceFacesDetails
      }
      face_match_votes {
        ...FaceMatchVoteFieldDetails
      }
    }
  }
  ${IdentityMatchingDetails}
  ${SelectedIdentityAlbumFolderTagDetailsForMatching}
  ${AlbumMatchingDetails}
  ${FaceMatchVoteFieldDetails}
  ${SelectedIdentityReferenceFacesDetails}
`;

export const LeaseFlushableVotes = gql`
  query leaseFlushableVotes($count: Int, $time_per_vote: Int) {
    leaseFlushableVotes(
      count: $count
      time_per_vote: $time_per_vote
      include_matcher_not_sure: true
      include_flusher_not_sure: true
    ) {
      lease_id
      lease_expires_at
      selected_identity {
        id
        identity {
          ...IdentityMatchingDetails
        }
        album_folder_tags {
          records {
            ...SelectedIdentityAlbumFolderTagDetailsForMatching
          }
        }
        album_membership {
          album {
            ...AlbumMatchingDetails
          }
        }
        ...SelectedIdentityReferenceFacesDetails
      }
      face_match_votes {
        ...FaceMatchVoteFieldDetails
      }
    }
  }
  ${IdentityMatchingDetails}
  ${SelectedIdentityAlbumFolderTagDetailsForMatching}
  ${AlbumMatchingDetails}
  ${FaceMatchVoteFieldDetails}
  ${SelectedIdentityReferenceFacesDetails}
`;

export const LeaseNumberRecVotes = gql`
  query albumForLeaseNumberRec($limit: Int = 50) {
    leaseHlPubBoxPhotos(count: $limit, time_per_photo: ${PUB_MATCHING_TIME_PER_VOTE_SEC}) {
      lease_id
      lease_expires_at
      photos {
        id
        thumbnail_urls {
          url
          size_code
          width
          height
        }
        pub_boxes {
          records {
            ...PubBoxDetails
          }
        }
      }
      album {
        id
        name
        creator {
          id
          email_address
          username
          name
          phone_number
          organization_name
          slug
        }
        album_folder_tags {
          records {
            id
            album_folder_tag {
              ... on IRecord {
                id
              }
              ... on IAlbumFolderTag {
                name
                type
              }
            }
            uniform_album_folder_tag_levels {
              records {
                id
                uniform_album_folder_tag_level {
                  id
                  name
                }
                uniform_album_folder_tag_level_jerseys {
                  records {
                    id
                    name
                    colors
                    venue
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  ${PubBoxDetails}
`;

export const LeaseNumRecSelectedIdentity = gql`
  query leaseNumRecSelectedIdentity(
    $time: Int
    $selectedIdentityIds: [RecordID] = []
  ) {
    leaseNumRecSelectedIdentity(
      time: $time
      selected_identity_ids: $selectedIdentityIds
    ) {
      lease_id
      lease_expires_at
      selected_identity {
        id
        identity {
          id
          ...IdentityMatchingDetails
          current_matching_face {
            id
          }
        }
        album_membership {
          album {
            ...AlbumMatchingDetails
          }
        }
        ...SelectedIdentityTagDetails
      }
    }
  }
  ${SelectedIdentityTagDetails}
  ${IdentityMatchingDetails}
  ${AlbumMatchingDetails}
`;

export const NumRecSelectedIdentityPhotos = gql`
  query numRecSelectedIdentityPhotos(
    $selectedIdentityId: RecordID!
    $offset: Int = 0
    $limit: Int = 100
    $colorSort: NumRecPhotosColorSortEnum = ENABLED
  ) {
    selectedIdentity(id: $selectedIdentityId) {
      id
      num_rec_photos(
        slice: { limit: $limit, offset: $offset }
        color_sort: $colorSort
      ) {
        total
        records {
          face {
            id
            facebox
          }
          photo {
            id
            thumbnail_urls {
              ...PhotoUrlTypeFields
            }
          }
          rank_score
          jersey_number
          face_match_vote {
            ...FaceMatchVoteFieldDetails
          }
          is_not_sure
        }
      }
    }
  }
  ${PhotoUrlTypeFields}
  ${FaceMatchVoteFieldDetails}
`;

export const LeaseSelectedIdentity = gql`
  query leaseSelectedIdentity($time: Int, $selectedIdentityId: RecordID!) {
    leaseSelectedIdentity(
      time: $time
      selected_identity_id: $selectedIdentityId
    ) {
      lease_id
      lease_expires_at
      selected_identity {
        id
        identity {
          id
          ...IdentityMatchingDetails
          current_matching_face {
            id
          }
        }
        album_membership {
          album {
            ...AlbumMatchingDetails
          }
        }
        ...SelectedIdentityTagDetails
        face_match_votes(
          slice: { limit: 999 }
          applied: true
          vote_filter: TOTALLY
        ) {
          records {
            ...FaceMatchVoteFieldDetails
          }
        }
      }
    }
  }
  ${SelectedIdentityTagDetails}
  ${IdentityMatchingDetails}
  ${AlbumMatchingDetails}
  ${FaceMatchVoteFieldDetails}
`;

export const ManualMatchAlbumPhotos = gql`
  query manualMatchAlbumPhotos(
    $albumId: RecordID!
    $offset: Int = 0
    $limit: Int = 100
  ) {
    album(id: $albumId) {
      id
      photos(
        sort_by_shoot_time: true
        include_unprocessed: false
        slice: { limit: $limit, offset: $offset }
      ) {
        total
        records {
          id
          thumbnail_urls(ignore_watermark: true) {
            ...PhotoUrlTypeFields
          }
          pub_boxes {
            records {
              ...PubBoxDetails
            }
          }
          faces {
            records {
              ...FaceDetails
            }
          }
        }
      }
    }
  }
  ${PhotoUrlTypeFields}
  ${PubBoxDetails}
  ${FaceDetails}
`;
